import React, {
  forwardRef,
  Fragment,
  ReactElement,
  Ref,
  useContext,
  useEffect,
  useState
} from 'react'
import { useNavigate } from 'react-router-dom'
import { Logout, Settings } from '@mui/icons-material'
import HikingIcon from '@mui/icons-material/Hiking'
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Slide from '@mui/material/Slide'
import Tooltip from '@mui/material/Tooltip'
import { TransitionProps } from '@mui/material/transitions'
import Typography from '@mui/material/Typography'
import AuthAPI from 'api/orbitV1/AuthAPI'
import profilePlaceholder from 'assets/placeholders/profile_placeholder.png'
import { PictureWrapper } from 'common/ui/colorBox'
import JoyrideContext from 'context/joyride/joyrideContext'
import OrbitSession from 'hooks/useCookies'
import PropTypes from 'prop-types' // Profile picture placeholder

import packageInfo from '../../../../../package.json'

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children: ReactElement<any, any>
  },
  ref: Ref<unknown>
) {
  return <Slide direction='up' ref={ref} {...props} />
})

export default function NavbarProfileMenu({ toggled }) {
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState(null)
  const [profilePic, setProfilePic] = useState<string>('')
  const [userName, setUserName] = useState<string>('')
  const [backendVersion, setBackendVersion] = useState<string>('')
  const [openDialog, setOpenDialog] = useState(false)
  const joyrideContext = useContext(JoyrideContext)
  const { setActive, setRun, setType } = joyrideContext
  const currRole = OrbitSession.get('role')

  const handleClickOpenDialog = () => {
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
  }

  const open = Boolean(anchorEl)
  const handleClick = (e) => {
    setAnchorEl(e.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const navigateToFaq = () => {
    handleCloseDialog()
    navigate('/app/faq')
  }

  const activateTour = () => {
    setRun(true)
    setActive(true)
  }

  const handleStartOnboardingTour = () => {
    handleCloseDialog()
    navigate('/app')
    setType('onboarding')
    setTimeout(() => {
      activateTour()
    }, 1000)
  }

  const handleStartBuySimTour = () => {
    handleCloseDialog()
    navigate('/app')
    setType('buyNewSim')
    activateTour()
  }

  const handleStartTrackOrderTour = () => {
    handleCloseDialog()
    navigate('/app')
    setType('trackOrder')
    activateTour()
  }

  const handleStartMigrateSimsTour = () => {
    handleCloseDialog()
    navigate('/app')
    setType('migrateSIMs')
    activateTour()
  }

  useEffect(() => {
    setUserName(OrbitSession.get('userName'))
    setBackendVersion(OrbitSession.get('backendVersion'))
    OrbitSession.setStoreType('localstorage')
    const profilePicUrl = OrbitSession.get('profileImg')
    OrbitSession.setStoreType('cookie')
    profilePicUrl
      ? setProfilePic('data:image/png;base64,' + profilePicUrl)
      : setProfilePic(profilePlaceholder)
  }, [])

  const logout = () => {
    OrbitSession.delAll()
    OrbitSession.setStoreType('localstorage')
    OrbitSession.del('profileImg')
    OrbitSession.setStoreType('cookie')
    try {
      AuthAPI.logUserOut()
    } finally {
      navigate('/login')
    }
  }

  const menuItems = [
    {
      clickHandler: () => navigate('profile'),
      icon: <Settings fontSize='small' />,
      label: 'Settings',
      role: ['ops', 'owner', 'opsowner', 'admin', 'opsviewer']
    },
    {
      clickHandler: handleClickOpenDialog,
      icon: <HikingIcon fontSize='small' />,
      label: 'Walkthrough',
      role: ['owner', 'admin']
    },
    {
      clickHandler: () => navigate('changelog'),
      icon: <TipsAndUpdatesIcon fontSize='small' />,
      label: 'Changelog',
      role: ['owner', 'admin']
    },
    {
      clickHandler: logout,
      icon: <Logout fontSize='small' data-cy='logout' />,
      label: 'Logout',
      role: ['ops', 'owner', 'opsowner', 'admin', 'opsviewer']
    }
  ]

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginLeft: 3
        }}
      >
        {toggled && (
          <Typography sx={{ color: 'white', margin: 0 }}>
            <b>{userName}</b>
          </Typography>
        )}
        <div id='sideNavBar_profileIcon'>
          <Tooltip title='Account settings' placement='right'>
            <IconButton
              onClick={handleClick}
              size='small'
              sx={{ ml: 2 }}
              aria-controls={open ? 'account-menu' : undefined}
              aria-haspopup='true'
              aria-expanded={open ? 'true' : undefined}
              data-cy='navIcon'
            >
              <PictureWrapper
                style={{
                  width: 32,
                  height: 32
                }}
                image={profilePic}
              />
            </IconButton>
          </Tooltip>
        </div>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id='account-menu'
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              mr: 1
            },
            // Revert to previous exact width
            width: '167.28px'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <MenuItem disabled>
          <Avatar alt={userName} src={profilePic} /> {userName}
        </MenuItem>

        <Divider light sx={{ width: '100%', height: '0.2px', margin: 0 }} />

        {menuItems.map(({ clickHandler, icon, label, role }, index) => {
          if (role.includes(currRole)) {
            return (
              <MenuItem
                id={label}
                key={`${label}-${index}`}
                onClick={clickHandler}
                data-cy={label}
              >
                <ListItemIcon>{icon}</ListItemIcon>
                {label}
              </MenuItem>
            )
          } else {
            return ''
          }
        })}

        <MenuItem disabled>
          <Typography
            sx={{
              color: 'black',
              margin: 0,
              borderTop: '1px solid white'
            }}
          >
            <strong>b</strong>: v{backendVersion} <strong>f</strong>: v
            {packageInfo.version}
          </Typography>
        </MenuItem>
      </Menu>

      <Dialog
        open={openDialog}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleCloseDialog}
        aria-describedby='alert-dialog-slide-description'
      >
        <DialogTitle textAlign='center' marginTop='30px'>
          Welcome to Orbit platform walk-through! 🌕🚀
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            textAlign='center'
            id='alert-dialog-slide-description'
          >
            Managing your business has never been this easy.
            <br /> Take a short tour around our website to learn more about our
            features or utilize one of the guided walkthroughs!
          </DialogContentText>
        </DialogContent>
        <span>
          <hr />
        </span>

        {[
          {
            header: 'First time user?',
            handler: handleStartOnboardingTour
          },
          {
            header: 'How do I buy new SIM(s)?',
            handler: handleStartBuySimTour
          },
          {
            header: 'How do I track my order status?',
            handler: handleStartTrackOrderTour
          },
          {
            header: 'How do I migrate SIMs?',
            handler: handleStartMigrateSimsTour
          }
        ].map(({ header, handler }, index) => {
          return (
            <Fragment key={`onboarding-tour-${index}`}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  marginTop: '10px',
                  marginBottom: 0
                }}
              >
                <div
                  style={{
                    width: '70%',
                    textAlign: 'right'
                  }}
                >
                  <h3>{header}</h3>
                </div>
                <div
                  style={{
                    width: '30%',
                    //  marginTop: '12px',
                    textAlign: 'center',
                    margin: 'auto',
                    display: 'block'
                  }}
                >
                  <Button variant='secondary' onClick={handler}>
                    GO
                  </Button>
                </div>
              </div>
            </Fragment>
          )
        })}
        <DialogActions>
          <Button onClick={navigateToFaq}>FAQ</Button>
          <Button onClick={handleCloseDialog}>EXIT</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

NavbarProfileMenu.propTypes = {
  toggled: PropTypes.bool
}
